import $ from 'jquery';
import 'bootstrap-sass/assets/javascripts/bootstrap/modal';
import {mapGetters} from 'vuex';
import t from '../main/i18n';
import {getLangcode, tagManagerTrack} from '../main/utils';
import {hasVariations, hasToggle} from './filters-config';
import components from '../algolia';
import ProductConfiguratorFilters from './product-configurator-filters';
import ProductConfiguratorResult from './product-configurator-result';
import ProductCard from '../product-card/ProductCard.vue';
import ShareSpec from './share-spec';

export default {
  name: 'ProductConfiguratorViewer',
  components: {
    ...components,
    ProductConfiguratorFilters,
    ProductConfiguratorResult,
    ShareSpec,
    ProductCard
  },
  props: {
    title: {type: String, required: true},
    productType: {type: String, required: true}
  },
  async created() {
    tagManagerTrack('view_item_list', {
      user: this.user?.accountNumber || 'guest',
      cartId: this.cartId,
      item_list_id: `plp_page_${this.productType}`,
      item_list_name: `plp_page_${this.productType}`
    });

    // Index name setup
    let level = 'product';
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlParamLevel = urlParams.get('level');
    if (urlParams.has('level')) {
      level = urlParams.get('level');
    }
    if (this.redirectToVariationsByDefault(urlParamLevel)) {
      level = 'variation';
    }
    this.level = level;
    this.indexName = this.getIndexName();


    // Routing setup
    const index = this.indexName;
    const useToggle = hasToggle(this.productType);
    const router = this.$router;

    const stateToRoute = uiState => {
      const indexUiState = uiState[index] || {};
      const optionalToggle = useToggle
        ? {toggle: indexUiState.toggle}
        : {};

      return {
        [index]: {
          refinementList: indexUiState.refinementList || {},
          query: indexUiState.query || '',
          ...optionalToggle
        }
      };
    };

    const routeToState = routeState => {
      const {query = '', refinementList = {}, toggle = {}} = routeState;

      const optionalToggle = useToggle ? {toggle} : {};
      return {
        [index]: {
          refinementList,
          query,
          ...optionalToggle
        }
      };
    };

    this.routing = {
      router: {
        read() {
          const {q: query = '', ...refinements} = JSON.parse(
            JSON.stringify(router.currentRoute.query)
          );

          let refinementList = {};
          let toggle = {};
          for (const [key, value] of Object.entries(refinements)) {
            if (Array.isArray(value)) {
              refinementList[key] = value.map(val =>
                decodeURIComponent(val)
              );
            } else if (value === 'true') {
              toggle[key] = true;
            } else {
              refinementList[key] = [decodeURIComponent(value)];
            }
          }

          const optionalToggle = useToggle ? {toggle} : {};

          return {refinementList, query, ...optionalToggle};
        },
        write(routeState) {
          window.history.replaceState(
            {},
            null,
            this.createURL(routeState)
          );
        },
        createURL(routeState) {
          const indexState = routeState[index] || {};
          const query = indexState.query || '';
          const refinements = indexState.refinementList || {};
          const toggle = indexState.toggle || {};
          let params = new URLSearchParams(query ? [['q', query]] : []);

          params.set('level', level);

          for (const [key, values] of Object.entries(refinements)) {
            for (const value of Array.from(values)) {
              if (key !== 'level') {
                params.append(key, value);
              }
            }
          }

          for (const [key] of Object.entries(toggle).filter(value => value)) {
            params.append(key, 'true');
          }

          const paramStr = params.toString().replace(/\+/g, '%20');
          if (!paramStr) return window.location.pathname;

          return `${window.location.pathname}?${paramStr}`;
        },
        onUpdate(cb) {
          this._onPopState = ({state}) => {
            const routeState = state;
            if (!routeState) {
              cb(this.read());
            } else {
              cb(routeState);
            }
          };
          window.addEventListener('popstate', this._onPopState);
        },
        dispose() {
          window.removeEventListener('popstate', this._onPopState);
          this.write();
        }
      },
      stateMapping: {
        stateToRoute,
        routeToState
      }
    };
  },
  data() {
    return {
      level: '',
      indexName: null,
      routing: null,
      scrollContainer: window,
      activeSpecLink: null,
      filteredFinish: null
    };
  },
  filters: {
    comma(value) {
      if (!value) return 'No';
      if (typeof value === 'string') value = Number(value);

      try {
        return new Intl.NumberFormat(this.langcode).format(value);
      } catch (e) {
        return value
          .toString()
          .replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      }
    }
  },
  mounted() {
    $(document).on('hidden.bs.modal', '.share-spec', () =>
      this.setSpecLink(null)
    );
    document.documentMode && document.documentElement.classList.add('msie');
  },
  updated() {
    $(document).on('hidden.bs.modal', '.share-spec', () =>
      this.setSpecLink(null)
    );
  },
  computed: {
    ...mapGetters('auth', ['user']),
    ...mapGetters('marketplace', ['cartId']),
    productTypeShouldRedirectToVariations() {
      const productTypes = [
        'bottle_filler', 'hand_dryer', 'mirror', 'showerhead', 'sink', 'soap_dispenser'
      ];

      return productTypes.includes(this.productType);
    },
    hasVariations() {
      return hasVariations(this.productType);
    },
    indices() {
      const indexPrefix = this.$algolia.indexPrefix
        + this.productType
        + (this.level === 'variation' ? '_variations_' : 's_');

      const titleSort = [
        {
          value: indexPrefix + 'title_asc',
          label: t('Title (A → Z)')
        },
        {
          value: indexPrefix + 'title_desc',
          label: t('Title (Z → A)')
        }
      ];

      const priceSort = [
        {
          value: indexPrefix + 'price_asc',
          label: t('Price (Low → High)')
        },
        {
          value: indexPrefix + 'price_desc',
          label: t('Price (High → Low)')
        }
      ];

      return this.shouldShowPriceSort ? titleSort.concat(priceSort) : titleSort;
    },
    shouldShowPriceSort() {
      return this.level === 'variation'
        && this.productType !== 'accessory'
        && this.productType !== 'bottle_filler'
        && this.productType !== 'hand_dryer'
        && this.productType !== 'sink'
        && this.productType !== 'soap_dispenser';
    },
    configuration() {
      let filters = '(NOT variation_discontinued:true) AND (NOT variation_omit_from_configurator:true)';
      if (this.productType === 'product')
        filters = `${filters} AND (variation_sustainable:"Yes")`;
      return {
        filters,
        hitsPerPage: 24,
        attributesToRetrieve: [
          'objectID',
          'product_id',
          'product_title',
          'product_url',
          'product_image',
          'product_finish_images',
          'product_lowest_price',
          'product_product_line',
          'variation_title',
          'variation_image',
          'variation_description',
          'variation_list_price',
          'variation_sku',
          'variation_discontinued',
          'variation_sustainable',
          'variation_type',
          'variation_part_number',
          'variation_channel_advisor_enabled',
          'variation_marketplace'
        ],
        attributesToHighlight: [
          'product_title',
          'product_description',
          'variation_title',
          'variation_description',
          'variation_sku',
          'variation_part_number'
        ]
      };
    },
    langcode() {
      return getLangcode(this.$route.params.langcode);
    }
  },
  watch: {
    indices() {
      this.resetScroll();
    }
  },
  methods: {
    redirectToVariationsByDefault(urlParamLevel) {
      return urlParamLevel !== 'product' && this.productTypeShouldRedirectToVariations;
    },
    getIndexName() {
      let productType = this.productType;
      let productTypeSuffix = 's';

      if (this.productType === 'accessory' && this.level === 'product') {
        productType = 'accessories';
        productTypeSuffix = '';
      }

      if (this.productType === 'lavatory' && this.level === 'product') {
        productType = 'lavatories';
        productTypeSuffix = '';
      }

      return this.$algolia.indexPrefix
        + productType
        + (this.level === 'variation' ? '_variations_title_asc' : productTypeSuffix);
    },
    setLevel(level) {
      let query = window.location.search;
      query = query.replace(`level=${this.level}`, `level=${level}`);
      window.location = `${query}`;
    },
    setSpecLink(link) {
      this.activeSpecLink = link;
    },
    resetScroll() {
      this.scrollContainer.scrollTo(0, 0);
    },
    toggleMobileFilters() {
      document.querySelector('.product-configurator-filters')
        .classList.toggle('mobile-visible');
    }
  }
};
